import React, { useState, useEffect, useCallback } from 'react';
import {
  X, Calendar, Clock, MapPin, DollarSign, Users, Trophy,
  CloudRain, Star, Flag, FileText, Mail, Shuffle, Eraser,
  ChevronDown, ChevronUp, ExternalLink
} from 'lucide-react';
import dayjs from 'dayjs';
import apiClient from '../utils/apiClient';
import { useAuth } from '../context/UnifiedAuthContext';
import { formatTime } from '../utils/formatTime';
import EventSignup from './EventSignup';
import EventWeather from './EventWeather';
import CourseModal from './CourseModal';
import PairingsModal from './PairingsModal';
import EmailComposer from './EmailComposer';
import recapsData from '../utils/recapsData';
import tippy from 'tippy.js';
import 'tippy.js/dist/tippy.css';

/**
 * EventDetailModal - Full event details in a modal
 *
 * Displays comprehensive event information:
 * - Event details (course, date, time, cost)
 * - Weather (for upcoming events)
 * - Player signup list
 * - Pairings
 * - Admin actions (for admins)
 * - Results (for past events)
 */
const EventDetailModal = ({
  event,
  isOpen,
  onClose,
  onEventUpdate,
  upcomingEvents = [],
}) => {
  const { hasRole, user } = useAuth();
  const [eventDetails, setEventDetails] = useState(null);
  const [eventPlayers, setEventPlayers] = useState([]);
  const [pairings, setPairings] = useState([]);
  const [scorecard, setScorecard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [weatherData, setWeatherData] = useState(null);
  const [showCourseModal, setShowCourseModal] = useState(false);
  const [selectedCourseDetails, setSelectedCourseDetails] = useState(null);
  const [showPairingsModal, setShowPairingsModal] = useState(false);
  const [showEmailComposer, setShowEmailComposer] = useState(false);
  const [emailType, setEmailType] = useState('pairings');
  const [mailingData, setMailingData] = useState(null);
  const [feedbackMessage, setFeedbackMessage] = useState(null);
  const [allPlayers, setAllPlayers] = useState([]);
  const [selectedPlayer, setSelectedPlayer] = useState('');
  const [showPlayersTable, setShowPlayersTable] = useState(true);

  const isUpcoming = !event?.closed;
  const isPast = event?.closed;

  // Check if this event has a recap
  const recapId = event ? dayjs(event.date).format('YYYY-MM-DD') : null;
  const eventRecap = recapId ? recapsData.find(r => r.id === recapId) : null;

  // Initialize tippy tooltips
  useEffect(() => {
    tippy('[data-tippy-content]');
  }, [eventPlayers]);

  // Fetch event details
  useEffect(() => {
    if (!event?.id || !isOpen) return;

    const fetchEventDetails = async () => {
      setLoading(true);
      try {
        const response = await apiClient.get(`/admin/events/${event.id}`);
        const eventData = response.data;

        // Fetch auth signups
        let authSignups = [];
        try {
          const signupsResponse = await apiClient.get(`/auth/events/${event.id}/signups`);
          authSignups = signupsResponse.data || [];
        } catch (err) {
          // Silently handle
        }

        setEventDetails({
          ...eventData.details,
          scorecard: eventData.scorecard,
          teeTime: eventData.details?.tee_time,
          numTimes: eventData.details?.num_teetimes,
          total_yardage: eventData.total_yardage,
          group_pairings: eventData.group_pairings,
        });

        // Combine players
        const existingPlayers = eventData.players || [];
        const combinedPlayers = [...existingPlayers];

        authSignups.forEach(signup => {
          const exists = combinedPlayers.find(p => p.player_id === signup.player_id);
          if (!exists) {
            combinedPlayers.push({
              player_id: signup.player_id,
              name: signup.player_name || `${signup.first_name} ${signup.last_name}`.trim() || signup.username,
              quota: null,
              current_quota: null,
              signup_date: signup.signup_date,
            });
          }
        });

        const normalizedPlayers = combinedPlayers.map(player => ({
          ...player,
          quota: player.player_quota || player.current_quota,
          scoreDiff: player.score - player.event_quota,
        }));

        // Sort based on event type
        if (isUpcoming) {
          setEventPlayers(normalizedPlayers.sort((a, b) => a.name.localeCompare(b.name)));
        } else {
          setEventPlayers(normalizedPlayers.sort((a, b) => b.scoreDiff - a.scoreDiff));
        }

        setScorecard(eventData.scorecard);

        // Fetch pairings
        const pairingsResponse = await apiClient.get(`/pairings/${event.id}`);
        setPairings(pairingsResponse.data || []);
      } catch (error) {
        console.error('Error fetching event details:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchEventDetails();
  }, [event?.id, isOpen, isUpcoming]);

  // Fetch all players for admin dropdown
  useEffect(() => {
    if (!isOpen || !hasRole('admin')) return;

    const fetchAllPlayers = async () => {
      try {
        const response = await apiClient.get('/players');
        setAllPlayers(response.data);
      } catch (error) {
        // Silently handle
      }
    };

    fetchAllPlayers();
  }, [isOpen, hasRole]);

  // Fetch mailing list for admin
  useEffect(() => {
    if (!isOpen || !(hasRole('admin') || hasRole('moderator'))) return;

    const fetchMailingList = async () => {
      try {
        const response = await apiClient.get('/admin/admin-players/mailing-list');
        setMailingData(response.data);
      } catch (error) {
        console.error('Failed to fetch mailing list:', error);
      }
    };

    fetchMailingList();
  }, [isOpen, hasRole]);

  const handleWeatherDataChange = useCallback((data) => {
    setWeatherData(data);
  }, []);

  // Available players for dropdown
  const availablePlayers = (allPlayers || []).filter(
    (player) => !(eventPlayers || []).some((ep) => ep.player_id === player.id)
  );

  // Handle add player
  const handleAddPlayer = async () => {
    if (!selectedPlayer || !event?.id) {
      alert('Please select a player to add.');
      return;
    }

    try {
      const response = await apiClient.post(`/admin/events/${event.id}/players`, {
        playerId: selectedPlayer,
      });

      const newPlayer = response.data;
      const normalizedPlayer = {
        ...newPlayer,
        player_id: newPlayer.id,
        quota: newPlayer.current_quota,
      };

      setEventPlayers((prev) => [...prev, normalizedPlayer].sort((a, b) => a.name.localeCompare(b.name)));

      if (pairings.length > 0) {
        await addPlayerToPairings(normalizedPlayer);
      }

      setSelectedPlayer('');
      setFeedbackMessage({ type: 'success', text: `Player "${normalizedPlayer.name}" added successfully!` });
      setTimeout(() => setFeedbackMessage(null), 3000);
    } catch (error) {
      console.error('Error adding player:', error);
      setFeedbackMessage({ type: 'error', text: 'Failed to add player. Please try again.' });
      setTimeout(() => setFeedbackMessage(null), 3000);
    }
  };

  // Add player to pairings
  const addPlayerToPairings = async (newPlayer) => {
    const updatedPairings = [...pairings];
    let targetGroupIndex = -1;

    for (let i = updatedPairings.length - 1; i >= 0; i--) {
      if (updatedPairings[i].length < 4) {
        targetGroupIndex = i;
        break;
      }
    }

    if (targetGroupIndex !== -1) {
      updatedPairings[targetGroupIndex].push(newPlayer);
    } else {
      updatedPairings.push([newPlayer]);
    }

    setPairings(updatedPairings);

    try {
      await apiClient.post(`/pairings/${event.id}`, { pairings: updatedPairings });
    } catch (error) {
      console.error('Error updating pairings:', error);
    }
  };

  // Handle delete player
  const handleDeletePlayer = async (playerId, playerName) => {
    try {
      await apiClient.delete(`/admin/events/${event.id}/players/${playerId}`);

      setEventPlayers((prev) => prev.filter((p) => p.player_id !== playerId));

      // Remove from pairings
      const updatedPairings = pairings
        .map((group) => group.filter((p) => p.player_id !== playerId))
        .filter((group) => group.length > 0);

      setPairings(updatedPairings);
      await savePairings(updatedPairings);

      setFeedbackMessage({ type: 'success', text: `Player ${playerName} removed from event.` });
      setTimeout(() => setFeedbackMessage(null), 3000);
    } catch (error) {
      console.error('Error deleting player:', error);
      setFeedbackMessage({ type: 'error', text: 'Failed to remove player.' });
      setTimeout(() => setFeedbackMessage(null), 3000);
    }
  };

  // Save pairings
  const savePairings = async (updatedPairings) => {
    try {
      await apiClient.post(`/pairings/${event.id}`, { pairings: updatedPairings });
      setPairings(updatedPairings);
    } catch (error) {
      setFeedbackMessage({ type: 'error', text: 'Failed to save pairings.' });
      setTimeout(() => setFeedbackMessage(null), 3000);
    }
  };

  // Generate pairings
  const openPairingsModal = () => {
    if (!eventPlayers || eventPlayers.length === 0) {
      alert('No players signed up for this event.');
      return;
    }

    const shuffledPlayers = [...eventPlayers].sort(() => Math.random() - 0.5);
    const newPairings = [];
    let i = 0;

    while (i < shuffledPlayers.length) {
      const remaining = shuffledPlayers.length - i;

      if (remaining >= 4 && (remaining % 4 === 0 || remaining % 4 >= 3)) {
        newPairings.push(shuffledPlayers.slice(i, i + 4));
        i += 4;
      } else if (remaining >= 3) {
        newPairings.push(shuffledPlayers.slice(i, i + 3));
        i += 3;
      } else {
        newPairings.push(shuffledPlayers.slice(i));
        break;
      }
    }

    const reorderedPairings = [
      ...newPairings.filter((g) => g.length === 3),
      ...newPairings.filter((g) => g.length === 4),
    ];

    setPairings(reorderedPairings);
    setShowPairingsModal(true);
  };

  // Clear pairings
  const clearPairings = async () => {
    try {
      await apiClient.post(`/pairings/${event.id}`, { pairings: [] });
      setPairings([]);
      setFeedbackMessage({ type: 'success', text: 'Pairings cleared successfully!' });
      setTimeout(() => setFeedbackMessage(null), 3000);
    } catch (error) {
      console.error('Error clearing pairings:', error);
      setFeedbackMessage({ type: 'error', text: 'Failed to clear pairings.' });
      setTimeout(() => setFeedbackMessage(null), 3000);
    }
  };

  // View course
  const handleViewCourse = async (courseId) => {
    try {
      const response = await apiClient.get(`/admin/courses/${courseId}`);
      setSelectedCourseDetails(response.data);
      setShowCourseModal(true);
    } catch (error) {
      console.error('Error fetching course details:', error);
    }
  };

  // Email handlers
  const handleGenerateEmail = (type) => {
    setEmailType(type);
    setShowEmailComposer(true);
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-4 md:inset-8 lg:inset-12 bg-white dark:bg-navy-900 rounded-2xl shadow-2xl z-50 overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex-shrink-0 bg-forest-700 dark:bg-navy-800 text-white p-4 md:p-6">
          <div className="flex items-start justify-between">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                {event.is_major && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-gold-400 text-forest-900">
                    <Star className="w-3 h-3" />
                    Major
                  </span>
                )}
                {!event.fedup_eligible && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-500 text-white">
                    Non-FedUp
                  </span>
                )}
                {event.rained_out && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-500 text-yellow-900">
                    <CloudRain className="w-3 h-3" />
                    Rainout
                  </span>
                )}
              </div>
              <h2 className="text-xl md:text-2xl font-bold truncate">
                {event.course_name}
              </h2>
              <div className="flex flex-wrap items-center gap-4 mt-2 text-forest-100">
                <div className="flex items-center gap-1.5">
                  <Calendar className="w-4 h-4" />
                  <span>{dayjs(event.date).format('dddd, MMMM D, YYYY')}</span>
                </div>
                {event.tee_time && (
                  <div className="flex items-center gap-1.5">
                    <Clock className="w-4 h-4" />
                    <span className="font-semibold text-gold-300">{formatTime(event.tee_time)}</span>
                  </div>
                )}
                {/* Recap Link for past events */}
                {isPast && eventRecap && (
                  <a
                    href={`/recaps/${eventRecap.id}`}
                    className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/20 hover:bg-white/30 text-white font-medium text-sm transition-colors"
                  >
                    <FileText className="w-4 h-4" />
                    Read Recap
                  </a>
                )}
              </div>
            </div>

            {/* Close Button */}
            <button
              onClick={onClose}
              className="flex-shrink-0 p-2 hover:bg-white/10 rounded-full transition-colors"
              aria-label="Close modal"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 md:p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-forest-500 border-t-transparent" />
            </div>
          ) : (
            <div className="space-y-6">
              {/* Event Signup for upcoming events */}
              {isUpcoming && (
                <div className="bg-gray-50 dark:bg-navy-800 rounded-xl p-4">
                  <EventSignup
                    event={event}
                    pairings={pairings}
                    onSignupChange={() => {
                      // Refresh event data
                      if (onEventUpdate) onEventUpdate();
                    }}
                  />
                </div>
              )}

              {/* Feedback Message */}
              {feedbackMessage && (
                <div
                  className={`p-3 rounded-lg ${
                    feedbackMessage.type === 'success'
                      ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                      : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                  }`}
                >
                  {feedbackMessage.text}
                </div>
              )}

              {/* Event Details & Weather Row */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Event Details */}
                <div className="bg-gray-50 dark:bg-navy-800 rounded-xl p-4">
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                    <Flag className="w-5 h-5 text-forest-600 dark:text-forest-400" />
                    Event Details
                  </h3>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500 dark:text-gray-400">Course</span>
                      <button
                        onClick={() => handleViewCourse(eventDetails?.course_id)}
                        className="font-medium text-forest-600 dark:text-forest-400 hover:underline"
                      >
                        {eventDetails?.course_name}
                      </button>
                    </div>
                    {eventDetails?.course_address && (
                      <div className="flex justify-between">
                        <span className="text-gray-500 dark:text-gray-400">Address</span>
                        <a
                          href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(eventDetails.course_address)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-medium text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1"
                        >
                          View Map <ExternalLink className="w-3 h-3" />
                        </a>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-gray-500 dark:text-gray-400">Tee Times</span>
                      <span className="font-medium text-gray-900 dark:text-white">
                        {eventDetails?.numTimes} booked
                      </span>
                    </div>
                    {eventDetails?.cost && (
                      <div className="flex justify-between">
                        <span className="text-gray-500 dark:text-gray-400">Cost</span>
                        <span className="font-medium text-emerald-600 dark:text-emerald-400">
                          ${Number(eventDetails.cost).toFixed(2)}
                        </span>
                      </div>
                    )}
                    {eventDetails?.front_tee && (
                      <div className="flex justify-between">
                        <span className="text-gray-500 dark:text-gray-400">Tees</span>
                        <span className="font-medium text-gray-900 dark:text-white">
                          {eventDetails.front_tee} / {eventDetails.back_tee}
                        </span>
                      </div>
                    )}
                    {eventDetails?.total_yardage && (
                      <div className="flex justify-between">
                        <span className="text-gray-500 dark:text-gray-400">Yardage</span>
                        <span className="font-medium text-gray-900 dark:text-white">
                          {eventDetails.total_yardage} yards
                        </span>
                      </div>
                    )}
                    {eventDetails?.notes && (
                      <div className="pt-2 border-t border-gray-200 dark:border-navy-700">
                        <span className="text-gray-500 dark:text-gray-400 block mb-1">Notes</span>
                        <p className="text-gray-700 dark:text-gray-300">{eventDetails.notes}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Weather - Only for upcoming */}
                {isUpcoming && eventDetails?.latitude && eventDetails?.longitude && (
                  <div className="bg-gray-50 dark:bg-navy-800 rounded-xl p-4">
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">
                      Weather Forecast
                    </h3>
                    <EventWeather
                      latitude={eventDetails.latitude}
                      longitude={eventDetails.longitude}
                      date={eventDetails.date}
                      teeTime={eventDetails.teeTime}
                      onWeatherDataChange={handleWeatherDataChange}
                    />
                  </div>
                )}
              </div>

              {/* Players Section */}
              <div className="bg-gray-50 dark:bg-navy-800 rounded-xl p-4">
                <button
                  onClick={() => setShowPlayersTable(!showPlayersTable)}
                  className="w-full flex items-center justify-between text-lg font-bold text-gray-900 dark:text-white mb-4"
                >
                  <span className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-forest-600 dark:text-forest-400" />
                    {isUpcoming ? 'Wankers Signed Up' : 'Results'} ({eventPlayers.length})
                  </span>
                  {showPlayersTable ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                </button>

                {showPlayersTable && (
                  <>
                    {/* Results Table for Past Events */}
                    {isPast && (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="bg-forest-700 dark:bg-navy-700 text-white">
                              <th className="p-3 text-center">Place</th>
                              <th className="p-3 text-left">Player</th>
                              <th className="p-3 text-center">Quota</th>
                              <th className="p-3 text-center">Score</th>
                              {!event.rained_out && <th className="p-3 text-center">+/-</th>}
                              <th className="p-3 text-center">CTPs</th>
                              <th className="p-3 text-center">Skins</th>
                              <th className="p-3 text-center">$ Won</th>
                              {event.fedup_eligible && <th className="p-3 text-center">Points</th>}
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200 dark:divide-navy-700">
                            {eventPlayers.map((player) => (
                              <tr key={player.player_id} className="hover:bg-gray-100 dark:hover:bg-navy-700/50">
                                <td className="p-3 text-center font-medium">{player.rank || '-'}</td>
                                <td className="p-3 text-left font-medium text-gray-900 dark:text-white">
                                  {player.name}
                                </td>
                                <td className="p-3 text-center">{player.event_quota}</td>
                                <td className="p-3 text-center font-semibold">{player.score}</td>
                                {!event.rained_out && (
                                  <td className="p-3 text-center">
                                    <span className={player.score - player.event_quota > 0 ? 'text-green-600' : 'text-red-600'}>
                                      {player.score - player.event_quota > 0 ? '+' : ''}{player.score - player.event_quota}
                                    </span>
                                  </td>
                                )}
                                <td className="p-3 text-center">{player.ctps || 0}</td>
                                <td className="p-3 text-center">{player.skins || 0}</td>
                                <td className="p-3 text-center text-emerald-600 font-medium">
                                  ${parseFloat(player.money_won || 0).toFixed(2)}
                                </td>
                                {event.fedup_eligible && (
                                  <td className="p-3 text-center font-semibold">
                                    {player.season_paid ? Number(player.total_points || 0).toFixed(0) : '-'}
                                  </td>
                                )}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {/* Signup Table for Upcoming Events */}
                    {isUpcoming && (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="bg-forest-700 dark:bg-navy-700 text-white">
                              <th className="p-3 text-center">#</th>
                              <th className="p-3 text-left">Name</th>
                              <th className="p-3 text-center">Quota</th>
                              {(hasRole('admin') || hasRole('moderator')) && (
                                <th className="p-3 text-right">Actions</th>
                              )}
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200 dark:divide-navy-700">
                            {eventPlayers.map((player, index) => (
                              <tr key={player.player_id} className="hover:bg-gray-100 dark:hover:bg-navy-700/50">
                                <td className="p-3 text-center">{index + 1}</td>
                                <td className="p-3 text-left font-medium text-gray-900 dark:text-white">
                                  {player.name}
                                </td>
                                <td className="p-3 text-center">{player.quota}</td>
                                {(hasRole('admin') || hasRole('moderator')) && (
                                  <td className="p-3 text-right">
                                    <button
                                      onClick={() => handleDeletePlayer(player.player_id, player.name)}
                                      className="px-2 py-1 bg-red-500 hover:bg-red-600 text-white rounded text-xs font-medium"
                                    >
                                      Remove
                                    </button>
                                  </td>
                                )}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Pairings Section - Only for upcoming */}
              {isUpcoming && pairings.length > 0 && (
                <div className="bg-gray-50 dark:bg-navy-800 rounded-xl p-4">
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                    <Users className="w-5 h-5 text-forest-600 dark:text-forest-400" />
                    Group Pairings
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-forest-700 dark:bg-navy-700 text-white">
                          {pairings.map((_, index) => (
                            <th key={index} className="p-3 text-center">
                              Group {index + 1}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {Array.from({ length: Math.max(...pairings.map((g) => g.length)) }).map((_, rowIndex) => (
                          <tr key={rowIndex} className="border-t border-gray-200 dark:border-navy-700">
                            {pairings.map((group, colIndex) => (
                              <td key={`${rowIndex}-${colIndex}`} className="p-3 text-center">
                                {group[rowIndex]?.name || ''}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Admin Actions - Only for admin/moderator on upcoming events */}
              {isUpcoming && (hasRole('admin') || hasRole('moderator')) && (
                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4 border border-blue-200 dark:border-blue-800">
                  <h3 className="text-lg font-bold text-blue-900 dark:text-blue-300 mb-4">
                    Admin Actions
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {hasRole('admin') && (
                      <>
                        <button
                          onClick={async () => {
                            const pairingsResponse = await apiClient.get(`/pairings/${event.id}`);
                            if (pairingsResponse.data.length > 0) {
                              setPairings(pairingsResponse.data);
                            } else {
                              openPairingsModal();
                            }
                            setShowPairingsModal(true);
                          }}
                          className="flex items-center justify-center gap-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg text-sm font-medium transition-colors"
                        >
                          <Shuffle className="w-4 h-4" />
                          {pairings.length > 0 ? 'Edit Pairings' : 'Generate Pairings'}
                        </button>
                        <button
                          onClick={clearPairings}
                          className="flex items-center justify-center gap-2 px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg text-sm font-medium transition-colors"
                        >
                          <Eraser className="w-4 h-4" />
                          Clear Pairings
                        </button>
                        <button
                          onClick={() => handleGenerateEmail('pairings')}
                          className="flex items-center justify-center gap-2 px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg text-sm font-medium transition-colors"
                        >
                          <Mail className="w-4 h-4" />
                          Pairings Email
                        </button>
                        <button
                          onClick={() => window.open(`/printScorecard?eventId=${event.id}`, '_blank')}
                          className="flex items-center justify-center gap-2 px-4 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-lg text-sm font-medium transition-colors"
                        >
                          <FileText className="w-4 h-4" />
                          Scoresheet
                        </button>
                      </>
                    )}
                    {hasRole('moderator') && (
                      <button
                        onClick={() => handleGenerateEmail('daily')}
                        className="flex items-center justify-center gap-2 px-4 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-lg text-sm font-medium transition-colors"
                      >
                        <Mail className="w-4 h-4" />
                        Signup Email
                      </button>
                    )}
                  </div>

                  {/* Add Player */}
                  {hasRole('admin') && (
                    <div className="mt-4 pt-4 border-t border-blue-200 dark:border-blue-800">
                      <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-300 mb-2">
                        Add Player
                      </h4>
                      <div className="flex items-center gap-2">
                        <select
                          value={selectedPlayer}
                          onChange={(e) => setSelectedPlayer(e.target.value)}
                          className="flex-1 border border-gray-300 dark:border-navy-600 rounded-lg p-2 bg-white dark:bg-navy-800 text-gray-900 dark:text-white text-sm"
                        >
                          <option value="">-- Select a Player --</option>
                          {availablePlayers.map((player) => (
                            <option key={player.id} value={player.id}>
                              {player.name}
                            </option>
                          ))}
                        </select>
                        <button
                          onClick={handleAddPlayer}
                          className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg text-sm font-medium transition-colors"
                        >
                          Add
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Sub-modals */}
      {showCourseModal && selectedCourseDetails && (
        <CourseModal
          course={selectedCourseDetails}
          onClose={() => {
            setShowCourseModal(false);
            setSelectedCourseDetails(null);
          }}
        />
      )}

      {showPairingsModal && (
        <PairingsModal
          pairings={pairings}
          setPairings={setPairings}
          onClose={() => setShowPairingsModal(false)}
          onSave={(updatedPairings) => {
            savePairings(updatedPairings);
          }}
        />
      )}

      {showEmailComposer && mailingData && (
        <EmailComposer
          isOpen={showEmailComposer}
          onClose={() => setShowEmailComposer(false)}
          recipients={(mailingData.playerEmailsString || mailingData.emailsString).split(', ').filter((email) => email.trim())}
          subject={(() => {
            const formatDateForSubject = (dateString) => {
              if (!dateString) return 'TBD';
              try {
                const date = new Date(dateString);
                if (isNaN(date.getTime())) return dateString;
                return date.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' });
              } catch {
                return dateString;
              }
            };
            const isDaily = emailType === 'daily';
            return isDaily
              ? `${formatDateForSubject(eventDetails?.date)} ${eventDetails?.course_name} Signups`
              : `${formatDateForSubject(eventDetails?.date)} ${eventDetails?.course_name} Group Pairings`;
          })()}
          eventDetails={eventDetails}
          pairings={pairings}
          eventPlayers={eventPlayers}
          upcomingEvents={upcomingEvents}
          weatherData={weatherData}
          adminUser={user}
          emailType={emailType}
          enableSendFeature={false}
        />
      )}
    </>
  );
};

export default EventDetailModal;
